import React, { createContext, useContext, useState, useEffect } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const CustomThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check localStorage for saved preference
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    // Save preference to localStorage
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  const toggleDarkMode = () => {
    setIsDarkMode(prev => !prev);
  };

  const theme = createTheme({
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
      primary: {
        main: '#174b6a',
        dark: '#0d3249',
        light: '#247BA0',
      },
      secondary: {
        main: '#82c0c7',
        dark: '#5a9ca3',
        light: '#a8d5da',
      },
      warning: {
        main: '#c6a862',
        dark: '#a88d45',
        light: '#d4be82',
      },
      info: {
        main: '#1863DC',
        dark: '#0056A7',
        light: '#4b9cdb',
      },
      background: {
        default: isDarkMode ? '#0a0a0a' : '#f4f6f8',
        paper: isDarkMode ? '#1a1a1a' : '#ffffff',
      },
      text: {
        primary: isDarkMode ? '#f0f0f0' : '#192529',
        secondary: isDarkMode ? '#b3b3b3' : '#555555',
      },
      grey: {
        50: isDarkMode ? '#fafafa' : '#fafafa',
        100: isDarkMode ? '#1a1a1a' : '#f5f5f5',
        200: isDarkMode ? '#2d2d2d' : '#e8ecef',
        300: isDarkMode ? '#404040' : '#d0d8df',
        400: isDarkMode ? '#595959' : '#8aa8b7',
        500: isDarkMode ? '#737373' : '#6b8a99',
        600: isDarkMode ? '#8c8c8c' : '#577580',
        700: isDarkMode ? '#a6a6a6' : '#3d5a6b',
        800: isDarkMode ? '#bfbfbf' : '#2a3f4d',
        900: isDarkMode ? '#d9d9d9' : '#1a2a35',
      },
    },
    typography: {
      fontFamily: '"Montserrat", "Helvetica Neue", sans-serif',
      h1: { fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 400, letterSpacing: '-0.02em' },
      h2: { fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 400, letterSpacing: '-0.01em' },
      h3: { fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 500, letterSpacing: '-0.01em' },
      h4: { fontWeight: 600 },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      subtitle1: { fontWeight: 500, letterSpacing: '0.01em' },
      subtitle2: { fontWeight: 500, letterSpacing: '0.01em' },
      body1: { fontWeight: 400, lineHeight: 1.7 },
      body2: { fontWeight: 400, lineHeight: 1.6 },
      button: { fontWeight: 600, textTransform: 'none', letterSpacing: '0.02em' },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: isDarkMode ? '#0a0a0a' : '#f4f6f8',
            transition: 'background-color 0.5s ease-out, color 0.5s ease-out',
          },
          '#root': {
            backgroundColor: isDarkMode ? '#0a0a0a' : '#f4f6f8',
            minHeight: '100vh',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: isDarkMode ? '#1a1a1a' : '#174b6a',
            color: '#ffffff',
            transition: 'background-color 0.5s ease-out',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: isDarkMode ? '#1a1a1a' : '#ffffff',
            borderColor: isDarkMode ? '#404040' : '#d0d8df',
            color: isDarkMode ? '#f0f0f0' : '#192529',
            boxShadow: isDarkMode
              ? '0 2px 12px rgba(0, 0, 0, 0.3)'
              : '0px 12px 18px -6px rgba(0, 0, 0, 0.1)',
            transition: 'box-shadow 0.5s ease-out, transform 0.3s ease-out, background-color 0.5s ease-out',
            '&:hover': {
              boxShadow: isDarkMode
                ? '0 4px 20px rgba(0, 0, 0, 0.4)'
                : '0px 16px 24px -8px rgba(23, 75, 106, 0.15)',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: isDarkMode ? '#1a1a1a' : '#ffffff',
            color: isDarkMode ? '#f0f0f0' : '#192529',
            transition: 'background-color 0.5s ease-out, box-shadow 0.5s ease-out',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 6,
            transition: 'all 0.3s ease-out',
          },
          contained: {
            boxShadow: '0 2px 8px rgba(24, 99, 220, 0.24)',
            '&:hover': {
              boxShadow: '0 4px 16px rgba(24, 99, 220, 0.32)',
              transform: 'translateY(-1px)',
            },
          },
          outlined: {
            borderWidth: '2px',
            '&:hover': {
              borderWidth: '2px',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 500,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 16,
            boxShadow: '0 24px 48px -12px rgba(0, 0, 0, 0.18)',
            border: '1px solid',
            borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(23, 75, 106, 0.08)',
            overflow: 'hidden',
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            padding: '20px 24px 16px',
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontWeight: 500,
            fontSize: '1.5rem',
            letterSpacing: '-0.01em',
            borderBottom: '1px solid',
            borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : '#e8ecef',
            backgroundColor: isDarkMode ? '#1a1a1a' : '#fafbfc',
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: '24px',
            '&.MuiDialogContent-dividers': {
              borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : '#e8ecef',
            },
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '16px 24px',
            borderTop: '1px solid',
            borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : '#e8ecef',
            backgroundColor: isDarkMode ? '#1a1a1a' : '#fafbfc',
            gap: 8,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            transition: 'background-color 0.3s ease-out',
          },
        },
      },
      MuiBox: {
        styleOverrides: {
          root: {
            '&.main-background': {
              backgroundColor: isDarkMode ? '#0a0a0a' : '#f4f6f8',
              minHeight: '100vh',
            },
          },
        },
      },
    },
  });

  const value = {
    isDarkMode,
    toggleDarkMode,
    theme,
  };

  return (
    <ThemeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

export default CustomThemeProvider;